import { DropdownOptionType } from '@ardoq/dropdown-menu';
import type { SettingsConfig } from '@ardoq/view-settings';
import { getSurface3DViewModifiers } from './menus';
import type { Surface3DViewProperties } from './types';

/**
 * What the settings panel shows beside a row: the label of that row's own active option.
 *
 * Read back out of `getSurface3DViewModifiers` rather than re-derived from the settings. The
 * row's tag and the tick inside the dropdown it opens then come from one table read twice, so
 * they cannot disagree - which is the failure the size-by picker had on the hierarchical views,
 * one panel naming a field the picture was not using.
 *
 * `buttonText`, which `menus.ts` sets on the field pickers, is not this: `ViewSettingsRow` reads
 * `label`, `options`, `isKeepOpen`, `dropdownSize` and `isDisabled`, and never `buttonText`. The
 * tag is the only place a current value appears in viewpoint mode.
 */
const toActiveOptionLabel = (
  modifiers: SettingsConfig[],
  modifierId: string
): string => {
  const modifier = modifiers.find(({ id }) => id === modifierId);

  /* `SettingsConfig` is a union and only its dropdown arm carries options, which is what this
     narrows on rather than casting. */
  if (!modifier || !('options' in modifier)) return '';

  const active = modifier.options.find(
    option =>
      option.type === DropdownOptionType.OPTION &&
      'isActive' in option &&
      option.isActive
  );

  return active && 'label' in active && typeof active.label === 'string'
    ? active.label
    : '';
};

const forModifier =
  (modifierId: string) =>
  (viewProperties: Record<string, unknown>): string =>
    toActiveOptionLabel(
      getSurface3DViewModifiers(
        viewProperties as unknown as Surface3DViewProperties
      ),
      modifierId
    );

/**
 * One entry per single-select row. `surface3dAppearance` is deliberately absent: it is a column
 * of toggles rather than a choice, so there is no one value to name beside it.
 */
export const surface3DDisplayValues = {
  surface3dFieldX: forModifier('surface3dFieldX'),
  surface3dFieldY: forModifier('surface3dFieldY'),
  surface3dFieldZ: forModifier('surface3dFieldZ'),
  surface3dInterpolation: forModifier('surface3dInterpolation'),
  surface3dAggregation: forModifier('surface3dAggregation'),
  surface3dResolution: forModifier('surface3dResolution'),
  surface3dColumnSize: forModifier('surface3dColumnSize'),
};
